import React, { useState } from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import {
  Alert,
  AlertTitle,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import { IMPORT_LAST_PUBLISHED_VERSION, IMPORT_OTHER_PUBLISHED_VERSION, IMPORT_WORD_DOCUMENT } from './content.graphql';
import LoadingSpinner from '../../LoadingSpinner';
import { ArrowBack, CloudDownload, Edit, FileDownload, MoreTime, MoreVert, Schedule, UploadFile, Visibility } from '@mui/icons-material';
import { Stack } from '@mui/system';
import { GuideEditor } from './tiptap/GuideEditor';
import { useGuideEditor } from './tiptap/useGuideEditor';
import { StyledPaper } from '../../theme';
import { useNavigate } from 'react-router-dom';
import { TiptapCollabConnectedUsers } from './tiptapCollab/TiptapCollabConnectedUsers';
import { TiptapCollabConnectionStatus } from './tiptapCollab/TiptapCollabConnectionStatus';
import { useModal } from '../../dialogs/useModal';
import { Release, useGuideCmsContext } from './GuideCmsContext';
import { useUpdateNumberedNodesOnEditor } from './tiptap/numbering';
import { VersionCommentDialog, VersionCommentDialogProps, VersionCommentDialogDialogResult } from './dialogs/CreateNewVersionDialog';
import { ConfirmDialog, ConfirmDialogProps } from '../../dialogs/ConfirmDialog';
import { useConfig } from '../../env';
import { useCommentsContext } from './sidebar/comments/CommentsContext';
import { HistoryDialog, HistoryDialogProps, HistoryDialogResult } from './history/HistoryDialog';
import { SnapshotStorage } from '@tiptap-pro/extension-snapshot';
import { ReferenceCandidateTypes } from './tiptap/extensions/plugins/ReferenceCandidateHighlight';
import { formatDateOnly } from '../../Formatters';
import { TiptapDocumentInfo } from './TiptapDocumentInfo';
import { TiptapDocumentItemFragment } from '../../__generated__/graphql';
import { ArchiveDocumentInfo } from './ArchiveDocumentInfo';
import { exportEditorToDocx } from './tiptap/docxExport';
import { exportEditorToMarkdown } from './tiptap/markdownExport';
import { ImportWordDocumentDialog, WordImportResult } from './dialogs/ImportWordDocumentDialog';

export const GuideCms: FC<{ isEditing: boolean }> = ({ isEditing }) => {
  const { guideId, editor, provider } = useGuideCmsContext();
  const historyModal = useModal<HistoryDialogProps, HistoryDialogResult>({ data: {} });

  const handleHistoricChangesClick = () => {
    historyModal.open({ guideId, editor, provider });
  };
  return (
    <>
      {historyModal.isOpen && <HistoryDialog {...historyModal} />}
      {isEditing ? <GuideCmsEditable onHistoricChangesClick={handleHistoricChangesClick} /> : <GuideCmsReadOnly />}
    </>
  );
};

const GuideCmsEditable: FC<{ onHistoricChangesClick: () => void }> = ({ onHistoricChangesClick }) => {
  const {
    isArchive,
    archivedVersion,
    guideId,
    isLoading,
    isValidSchema,
    guide,
    tiptapDocument,
    release,
    setEditor,
    setProvider,
    forceRefreshEditors,
    openInternalLinksDialog,
    openGuideLinkDialog,
    openStandardLinkDialog,
  } = useGuideCmsContext();
  const config = useConfig();
  const { closeComments } = useCommentsContext();
  const [importLastPublishedVersionError, setImportLastPublishedVersionError] = useState<boolean>(false);
  const [wordImportDialogOpen, setWordImportDialogOpen] = useState(false);
  const [wordImportResult, setWordImportResult] = useState<WordImportResult>();
  const [wordImportError, setWordImportError] = useState<string>();

  const handleReferenceCandidateClick = async (text: string, type: ReferenceCandidateTypes) => {
    if (type === 'guide') {
      await openGuideLinkDialog(text);
    } else if (type === 'standard') {
      await openStandardLinkDialog(text);
    } else {
      await openInternalLinksDialog(text);
    }
  };
  const isCurrentTiptapDocument = guide?.tiptapDocument?.documentName === tiptapDocument?.documentName;
  const tiptapDocumentIndex = guide?.tiptapDocuments?.findIndex((x) => x?.documentName === tiptapDocument?.documentName) ?? -1;
  let replacedByTiptapDocument: TiptapDocumentItemFragment | undefined;
  if (tiptapDocumentIndex > -1) {
    replacedByTiptapDocument = guide?.tiptapDocuments?.[tiptapDocumentIndex + 1] ?? undefined;
  }
  const { editor, tiptapCollab } = useGuideEditor({
    onEditorCreated: (editor, provider) => {
      setEditor(editor);
      if (provider) {
        setProvider(provider);
      }
    },
    isEditable: isCurrentTiptapDocument || isArchive,
    onReferenceCandidateClick: handleReferenceCandidateClick,
  });

  const collabHistory = editor?.storage.snapshot as SnapshotStorage;

  const updateNumberedNodesOnEditor = useUpdateNumberedNodesOnEditor();

  const [importLastPublishedVersion, { loading: isLoadingLastPublishedVersion }] = useMutation(IMPORT_LAST_PUBLISHED_VERSION);
  const [importOtherPublishedVersion, { loading: isLoadingOtherPublishedVersion }] = useMutation(IMPORT_OTHER_PUBLISHED_VERSION);
  const [importWordDocument, { loading: isImportingWordDocument }] = useMutation(IMPORT_WORD_DOCUMENT);

  const isImportingLastPublishedVersion = isLoadingLastPublishedVersion || isLoadingOtherPublishedVersion;

  const onCreateNewVersionClick = async () => {
    await versionCommentDialog.open();
  };

  const onImportLastPublishedVersionClick = async (version?: string, importVersion?: string) => {
    if (!editor) return;
    try {
      editor.commands.clearContent();
      setImportLastPublishedVersionError(false);
      if (importVersion) {
        await importOtherPublishedVersion({
          variables: { input: { guideId, editVersion: version, importVersion } },
        });
      } else {
        await importLastPublishedVersion({
          variables: { input: { guideId, version } },
        });
      }

      updateNumberedNodesOnEditor(editor);
      forceRefreshEditors();
      closeComments();
    } catch (e) {
      if (e instanceof ApolloError) {
        console.error('Failed to load legacy content', e);
      } else {
        console.error('Failed to set legacy content', e);
      }
      setImportLastPublishedVersionError(true);
    }
  };

  const openWordImportDialog = () => {
    setWordImportResult(undefined);
    setWordImportError(undefined);
    setWordImportDialogOpen(true);
  };

  const closeWordImportDialog = () => {
    if (isImportingWordDocument) return;
    setWordImportDialogOpen(false);
    setWordImportResult(undefined);
    setWordImportError(undefined);
  };

  const onImportWordDocument = async (file: File) => {
    setWordImportError(undefined);
    setWordImportResult(undefined);
    try {
      const response = await importWordDocument({ variables: { input: { guideId, file } } });
      const result = response.data?.guideImportWordDocument.cmsWordImportResult;
      if (!result) {
        setWordImportError('Tjenesten returnerte ikke et importresultat. Dokumentet er ikke endret.');
        return;
      }

      setWordImportResult({
        success: result.success,
        imageCount: result.imageCount,
        diagnostics: result.diagnostics ?? [],
      });
    } catch (error) {
      console.error('Failed to import Word document', error);
      setWordImportError('Klarte ikke å importere Word-filen. Dokumentet er ikke endret.');
    }
  };

  const otherCmsReleases =
    guide?.releases
      ?.filter((r) => r?.id !== release?.id && !!r?.guide?.tiptapDocument)
      .reduce<Release[]>((releases, rel) => {
        if (!releases.some((r) => r?.publicVersion === rel?.publicVersion)) {
          releases.push(rel);
        }
        return releases;
      }, []) ?? [];

  const [isExportingDocx, setIsExportingDocx] = useState(false);
  const [isExportingMarkdown, setIsExportingMarkdown] = useState(false);
  const versionCommentDialog = useModal<VersionCommentDialogProps, VersionCommentDialogDialogResult>({ data: {} });
  const importConfirmDialog = useModal<ConfirmDialogProps, boolean>({
    data: {
      title: 'Erstatte innhold?',
      message: (
        <>
          Er du sikker på at du vil last inn nytt innhold?
          <br />
          Dette vil overskrive det gjeldende innholdet i editoren.
        </>
      ),
      submitText: 'Erstatt innhold',
      cancelText: 'Avbryt',
    },
  });
  const openChangeHistoryModal = async () => {
    onHistoricChangesClick();
  };
  const handleExportDocx = async () => {
    if (!editor || !config) return;
    setIsExportingDocx(true);
    try {
      const filename = `${guide?.docName || 'dokument'}.docx`;
      await exportEditorToDocx(editor, config, guideId, filename, guide?.docName || '', guide?.docTitle || '');
    } catch (error) {
      console.error('Failed to export DOCX', error);
    } finally {
      setIsExportingDocx(false);
    }
  };
  const handleExportMarkdown = () => {
    if (!editor || !config) return;
    setIsExportingMarkdown(true);
    try {
      const filename = `${guide?.docName || 'dokument'}.md`;
      exportEditorToMarkdown(editor, config, guideId, filename, guide?.docName || '', guide?.docTitle || '');
    } catch (error) {
      console.error('Failed to export Markdown', error);
    } finally {
      setIsExportingMarkdown(false);
    }
  };
  return (
    <StyledPaper>
      {versionCommentDialog.isOpen && <VersionCommentDialog {...versionCommentDialog} />}
      {importConfirmDialog.isOpen && <ConfirmDialog modal={importConfirmDialog} />}
      <ImportWordDocumentDialog
        open={wordImportDialogOpen}
        importing={isImportingWordDocument}
        result={wordImportResult}
        error={wordImportError}
        onClose={closeWordImportDialog}
        onImport={onImportWordDocument}
      />
      <Stack gap={2}>
        {!isLoading && (
          <Stack direction="row" flexWrap="wrap" alignItems="center" justifyContent="space-between">
            <Stack direction="row" gap={1} alignItems="center">
              <Title />
            </Stack>
            <Stack direction="row" sx={{ pt: 1, pb: 1 }} spacing={1} alignItems={'center'}>
              <DocumentSelect />
              {!isCurrentTiptapDocument && (
                <ActionsMenu>
                  <MenuItem
                    disabled={collabHistory?.versions?.length === 0}
                    onClick={() => {
                      openChangeHistoryModal();
                    }}
                  >
                    <ListItemIcon>
                      <Schedule fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Tidligere versjoner av innholdet...</ListItemText>
                  </MenuItem>
                </ActionsMenu>
              )}
            </Stack>
          </Stack>
        )}
        {!isImportingLastPublishedVersion && importLastPublishedVersionError && (
          <Alert severity="error" onClose={() => setImportLastPublishedVersionError(false)}>
            Klarte ikke laste innhold fra sist publiserte anvisning. Vennligst prøv igjen.
          </Alert>
        )}
        {isArchive && <ArchiveDocumentInfo />}
        {!isCurrentTiptapDocument && !isArchive && tiptapDocument && (
          <TiptapDocumentInfo tiptapDocument={tiptapDocument} replacedByTiptapDocument={replacedByTiptapDocument} />
        )}
        {!isValidSchema && (
          <Alert
            severity="error"
            action={
              <Button variant="contained" color="error" onClick={() => window.location.reload()}>
                Last siden på nytt
              </Button>
            }
          >
            <AlertTitle>Gammel versjon av CMS</AlertTitle>
            Du kjører en utdatert versjon av CMS dataformatet. Vennligst last siden på nytt for å fortsette på nyeste format.
          </Alert>
        )}
        {(isLoading || isImportingLastPublishedVersion) && <LoadingSpinner />}
        {!isLoading && !isImportingLastPublishedVersion && editor && (
          <GuideEditor
            guideId={guideId}
            editor={editor}
            menuComponent={
              <Stack direction="row" flexWrap="wrap">
                <Stack direction="row" gap={1} marginLeft="auto" alignItems="center">
                  <Stack direction="row" gap={2}>
                    <TiptapCollabConnectionStatus connectionStatus={tiptapCollab.connectionStatus} />
                    <TiptapCollabConnectedUsers users={tiptapCollab.connectedUsers} />
                  </Stack>
                  <PreviewButton />

                  <Button
                    variant="contained"
                    disabled={!tiptapCollab.connectionStatus.isConnected}
                    onClick={onCreateNewVersionClick}
                    startIcon={<MoreTime />}
                  >
                    Ny versjon...
                  </Button>

                  <ActionsMenu>
                    <PreviewMenuItem />

                    {isCurrentTiptapDocument && !isArchive && (
                      <MenuItem disabled={isImportingWordDocument} onClick={openWordImportDialog}>
                        <ListItemIcon>
                          <UploadFile fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Importer Word-fil...</ListItemText>
                      </MenuItem>
                    )}

                    <MenuItem
                      disabled={collabHistory?.versions?.length === 0}
                      onClick={() => {
                        openChangeHistoryModal();
                      }}
                    >
                      <ListItemIcon>
                        <Schedule fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Tidligere versjoner av innholdet...</ListItemText>
                    </MenuItem>

                    <MenuItem
                      disabled={isImportingLastPublishedVersion}
                      onClick={async () => {
                        const confirmed = await importConfirmDialog.open();
                        if (confirmed) {
                          onImportLastPublishedVersionClick(archivedVersion);
                        }
                      }}
                    >
                      <ListItemIcon>
                        <CloudDownload fontSize="small" />
                      </ListItemIcon>
                      {isArchive ? (
                        <ListItemText>Last inn publisert anvisning{isArchive && ` (${archivedVersion})`}</ListItemText>
                      ) : (
                        <ListItemText>Last inn sist publiserte anvisning</ListItemText>
                      )}
                    </MenuItem>
                    <ListSubheader>Eksporter</ListSubheader>
                    <MenuItem disabled={isExportingDocx} onClick={handleExportDocx}>
                      <ListItemIcon>
                        <FileDownload fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>{isExportingDocx ? 'Eksporterer...' : 'Word (.docx)'}</ListItemText>
                    </MenuItem>

                    <MenuItem disabled={isExportingMarkdown} onClick={handleExportMarkdown}>
                      <ListItemIcon>
                        <FileDownload fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>{isExportingMarkdown ? 'Eksporterer...' : 'Markdown (.md)'}</ListItemText>
                    </MenuItem>

                    {archivedVersion && otherCmsReleases?.length > 0 && (
                      <ListSubheader>Kopier innhold fra andre publiserte anvisninger</ListSubheader>
                    )}
                    {archivedVersion &&
                      otherCmsReleases?.length > 0 &&
                      otherCmsReleases.map((r) => (
                        <MenuItem
                          key={r?.id}
                          disabled={isImportingLastPublishedVersion}
                          onClick={async () => {
                            if (archivedVersion && r?.publicVersion) {
                              const confirmed = await importConfirmDialog.open();
                              if (confirmed) {
                                onImportLastPublishedVersionClick(archivedVersion, r?.publicVersion);
                              }
                            }
                          }}
                        >
                          <ListItemIcon>
                            <CloudDownload fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Kopier versjon {r?.publicVersion}</ListItemText>
                        </MenuItem>
                      ))}
                  </ActionsMenu>
                </Stack>
              </Stack>
            }
          />
        )}
      </Stack>
    </StyledPaper>
  );
};

const GuideCmsReadOnly: FC = () => {
  const navigate = useNavigate();
  const config = useConfig();
  const { guideId, isLoading, guide, setEditor } = useGuideCmsContext();
  const { editor: readOnlyEditor } = useGuideEditor({
    onEditorCreated: (editor) => {
      setEditor(editor);
    },
    isEditable: false,
  });
  const [isExportingDocx, setIsExportingDocx] = useState(false);
  const [isExportingMarkdown, setIsExportingMarkdown] = useState(false);

  const onEditClick = async () => {
    navigate('./edit', { relative: 'path', replace: true });
  };
  const handleExportDocx = async () => {
    if (!readOnlyEditor || !config) return;
    setIsExportingDocx(true);
    try {
      const filename = `${guide?.docName || 'dokument'}.docx`;
      await exportEditorToDocx(readOnlyEditor, config, guideId, filename, guide?.docName || '', guide?.docTitle || '');
    } catch (error) {
      console.error('Failed to export DOCX', error);
    } finally {
      setIsExportingDocx(false);
    }
  };
  const handleExportMarkdown = () => {
    if (!readOnlyEditor || !config) return;
    setIsExportingMarkdown(true);
    try {
      const filename = `${guide?.docName || 'dokument'}.md`;
      exportEditorToMarkdown(readOnlyEditor, config, guideId, filename, guide?.docName || '', guide?.docTitle || '');
    } catch (error) {
      console.error('Failed to export Markdown', error);
    } finally {
      setIsExportingMarkdown(false);
    }
  };

  return (
    <StyledPaper>
      <Stack gap={2}>
        {!isLoading && (
          <Stack direction="row" flexWrap="wrap" alignItems="center">
            <Stack direction="row" gap={1} alignItems="center">
              <Title />
            </Stack>
            <Stack direction="row" gap={2} marginLeft="auto" alignItems="center">
              <Button variant="contained" onClick={onEditClick} startIcon={<Edit />}>
                Endre
              </Button>

              <ActionsMenu>
                <PreviewMenuItem />
                <ListSubheader>Eksporter</ListSubheader>
                <MenuItem disabled={isExportingDocx} onClick={handleExportDocx}>
                  <ListItemIcon>
                    <FileDownload fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{isExportingDocx ? 'Eksporterer...' : 'Word (.docx)'}</ListItemText>
                </MenuItem>
                <MenuItem disabled={isExportingMarkdown} onClick={handleExportMarkdown}>
                  <ListItemIcon>
                    <FileDownload fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>{isExportingMarkdown ? 'Eksporterer...' : 'Markdown (.md)'}</ListItemText>
                </MenuItem>
              </ActionsMenu>
            </Stack>
          </Stack>
        )}
        {isLoading && <LoadingSpinner />}
        {!isLoading && readOnlyEditor && <GuideEditor guideId={guideId} editor={readOnlyEditor} />}
      </Stack>
    </StyledPaper>
  );
};

const ActionsMenu = ({ children }: { children: React.ReactNode }) => {
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };
  const closeMenu = () => {
    setMenuAnchorEl(null);
  };
  return (
    <div>
      <IconButton onClick={openMenu}>
        <MoreVert />
      </IconButton>
      <Menu open={isMenuOpen} onClick={closeMenu} onClose={closeMenu} anchorEl={menuAnchorEl} disableScrollLock>
        {children}
      </Menu>
    </div>
  );
};

const usePreviewUrl = () => {
  const config = useConfig();
  const { guide, release, isArchive } = useGuideCmsContext();
  if (isArchive) {
    return `${config?.portalUrl}/dokument/${guide?.docId}?mode=preview&version=${release?.publicVersion}`;
  }
  return `${config?.portalUrl}/dokument/${guide?.docId}?mode=preview`;
};

const PreviewButton: FC = () => {
  const previewUrl = usePreviewUrl();

  return (
    <Tooltip title="Åpne forhåndsvisning">
      <IconButton href={previewUrl} component="a" target="_blank">
        <Visibility />
      </IconButton>
    </Tooltip>
  );
};

const PreviewMenuItem: FC = () => {
  const previewUrl = usePreviewUrl();

  return (
    <MenuItem href={previewUrl} component="a" target="_blank">
      <ListItemIcon>
        <Visibility />
      </ListItemIcon>
      <ListItemText>Forhåndsvisning</ListItemText>
    </MenuItem>
  );
};

const Title: FC = ({}) => {
  const navigate = useNavigate();
  const { guideId, guide, editor, isArchive, snapshotState } = useGuideCmsContext();
  if (!editor) {
    return null;
  }

  const showVersion = snapshotState.versions.length > 0;
  const latestVersion = snapshotState.latestVersion;

  const handleBack = () => {
    if (isArchive) {
      navigate(-1); // Go back in history to preserve URL params
    } else {
      navigate(`/guide/${guideId}`);
    }
  };

  return (
    <Typography variant="h5">
      <IconButton onClick={handleBack} size="large">
        <ArrowBack />
      </IconButton>{' '}
      {guide?.docName} {guide?.docTitle} {showVersion && <>(v. {latestVersion})</>}
    </Typography>
  );
};

const DocumentSelect: FC = () => {
  const { guide, tiptapDocument, setTiptapDocument, isArchive } = useGuideCmsContext();
  const documents = guide?.tiptapDocuments ?? [];
  if (documents.length <= 1) return null;

  const handleOnTiptapDocumentChange = (documentName: string) => {
    const document = documents.find((d) => d?.documentName === documentName);
    if (document) {
      setTiptapDocument(document);
    }
  };
  if (isArchive) return null;
  return (
    <FormControl>
      <InputLabel id="tiptap-document-select-label">Dokument</InputLabel>
      <Select
        labelId="tiptap-document-select-label"
        size="small"
        value={tiptapDocument?.documentName}
        label="Dokument"
        onChange={(e) => handleOnTiptapDocumentChange(e.target.value ?? '')}
      >
        {[...documents].reverse().map((doc) => {
          const isExpired = doc?.documentName !== guide?.tiptapDocument?.documentName;
          return (
            <MenuItem key={doc?.documentName} value={doc?.documentName ?? ''}>
              {isExpired ? `Utgått dokument ${formatDateOnly(doc?.createdAt)}` : `Gjeldende dokument`}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};
